import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  PointOfSale as POSIcon,
  TableRestaurant as TableIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, deleteDoc, deleteField, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ja } from 'date-fns/locale';
import { nowInShopTimezone, getBusinessDateRange , resolveBoundaryHour } from '../utils/businessDate';
import { resolveTaxRate } from '../utils/taxRate';

import { pctToRate } from '../utils/numberGuard';
interface BackDetail {
  checkoutId: string;
  checkoutType: 'pos' | 'table';
  customerName: string;
  tableName?: string;
  total: number;
  staffId: string;
  staffName: string;
  shareRate: number;
  backRate: number;
  backAmount: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  approvedAt?: Date;
  approvedBy?: string;
  items?: any[];
}

interface UnassignedCheckout {
  checkoutId: string;
  checkoutType: 'pos' | 'table';
  customerName: string;
  tableName?: string;
  total: number;
  items?: any[];
  createdAt: Date;
}

interface StaffSummary {
  staffId: string;
  staffName: string;
  pendingCount: number;
  pendingAmount: number;
  approvedCount: number;
  approvedAmount: number;
}

// インセンティブ明細
interface IncentiveDetail {
  id: string;
  staffId: string;
  staffName: string;
  date: string;
  checkoutId: string;
  checkoutType: string;
  grossAmount: number;
  checkoutTotal: number;
  netSubtotal: number;
  costDeduction: number;
  incentiveBase: number;
  shareRate: number;
  staffIncentiveBase: number;
  planContribution: number;
  /** 承認の状態。累進モードでも承認できるようにするために要る。 */
  backStatus?: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
}

// インセンティブスタッフサマリー
interface IncentiveStaffSummary {
  staffId: string;
  staffName: string;
  totalIncentiveBase: number;
  checkoutCount: number;
  daysWorked: number;
  dailyAverage: number;
  planContribution: number;
}


/**
 * 会計文書1件から、スタッフ別のバック明細を作る。
 *
 * v4.0.0 で直したこと:
 *  1. shareRate の単位ずれ。会計側は小数(0.5)で書くのに、ここは
 *     `(staff.shareRate || 100) / 100` と百分率で読んでいたため、
 *     表示されるバック額が正の 1/100 になっていた。
 *     さらに `|| 100` により取り分0%のキャストが100%扱いになっていた。
 *  2. バックの母数。gross の total ではなく、原価と利益控除を引いた
 *     salesAllocation.resolved（＝キャストへ実際に帰属した額）を使う。
 *     アプリ側のキャスト画面と同じ母数になる。
 */
function extractBackLines(
  data: any,
  defaultBackRate: number
): { staffId: string; staffName: string; shareRate: number; backRate: number; backAmount: number; base: number }[] {
  const allocations = data?.salesAllocation?.allocations as any[] | undefined;
  const netSales = data?.salesAllocation?.netSales ?? data?.total ?? 0;
  const salesStaff = (data?.salesStaff || []) as any[];

  // 会計が書いた確定額があればそれを使う（単位の解釈が要らない）
  if (allocations && allocations.length > 0) {
    return allocations
      .filter((a) => a.staffId && a.staffId !== '__shop__')
      .map((a) => {
        const matched = salesStaff.find((s) => s.staffId === a.staffId);
        const backRate = matched?.backRate ?? defaultBackRate;
        const base = a.resolved ?? 0;
        return {
          staffId: a.staffId,
          staffName: a.staffName || matched?.staffName || '',
          shareRate: netSales > 0 ? base / netSales : 0,
          backRate,
          base,
          backAmount: Math.round(base * backRate),
        };
      });
  }

  // 旧データ用のフォールバック。
  // 1 以下なら小数、1 超なら百分率とみなす（0 は 0 のまま。100% に化けさせない）。
  return salesStaff
    .filter((s) => s.staffId && s.staffId !== '__shop__')
    .map((s) => {
      const raw = typeof s.shareRate === 'number' ? s.shareRate : 1;
      const share = raw > 1 ? raw / 100 : raw;
      const backRate = s.backRate ?? defaultBackRate;
      const base = netSales * share;
      return {
        staffId: s.staffId,
        staffName: s.staffName || '',
        shareRate: share,
        backRate,
        base,
        backAmount: Math.round(base * backRate),
      };
    });
}

const BackApproval: React.FC = () => {
  const { shop } = useAuth();
  const [backDetails, setBackDetails] = useState<BackDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [filterStaff, setFilterStaff] = useState<string>('all');
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const shopNow = nowInShopTimezone(shop?.timezone);
  // useState の初期値は初回の描画でしか評価されない。
  // その時点では shop がまだ null なので、店のタイムゾーンが決まる前に
  // 日付が固まり、以後どれだけ待っても入らない。
  // 結果として期間が空のまま「データがありません」になり、
  // 承認できる明細が1件も出せなかった。
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // 店が読み込まれた時点で、その店の暦で今月を入れる
  useEffect(() => {
    if (!shop || startDate) return;
    const now = nowInShopTimezone(shop.timezone);
    setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
  }, [shop, startDate]);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; action: 'approve' | 'reject' | null }>({
    open: false,
    action: null,
  });
  const [staffSummaries, setStaffSummaries] = useState<StaffSummary[]>([]);

  // インセンティブ関連state
  const [incentiveDetails, setIncentiveDetails] = useState<IncentiveDetail[]>([]);
  const [incentiveStaffSummaries, setIncentiveStaffSummaries] = useState<IncentiveStaffSummary[]>([]);
  const isIncentiveMode = shop?.incentiveSettings?.enabled || false;

  // インセンティブ編集・削除state
  const [editDialog, setEditDialog] = useState<{ open: boolean; detail: IncentiveDetail | null }>({ open: false, detail: null });
  const [editShareRate, setEditShareRate] = useState<number>(100);
  const [editIncentiveBase, setEditIncentiveBase] = useState<number>(0);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; detail: IncentiveDetail | null }>({ open: false, detail: null });

  // バック編集・削除state
  const [editBackDialog, setEditBackDialog] = useState<{ open: boolean; detail: BackDetail | null }>({ open: false, detail: null });
  const [editBackShareRate, setEditBackShareRate] = useState(100);
  const [editBackBackRate, setEditBackBackRate] = useState(50);
  const [deleteBackDialog, setDeleteBackDialog] = useState<{ open: boolean; detail: BackDetail | null }>({ open: false, detail: null });

  // スタッフ未設定会計
  const [unassignedCheckouts, setUnassignedCheckouts] = useState<UnassignedCheckout[]>([]);
  const [assignDialog, setAssignDialog] = useState<{ open: boolean; checkout: UnassignedCheckout | null }>({ open: false, checkout: null });
  const [assignStaffSelections, setAssignStaffSelections] = useState<{ staffId: string; staffName: string; shareRate: number }[]>([]);
  const [allStaffList, setAllStaffList] = useState<{ id: string; name: string }[]>([]);

  // 店舗のスタッフ一覧を取得
  useEffect(() => {
    if (!shop) return;
    const fetchAllStaff = async () => {
      try {
        // 売上スタッフは /employees を正とする（会計側と揃える）
        const staffQuery = query(
          collection(db, 'employees'),
          where('shopId', '==', shop.id),
          where('status', '==', 'active')
        );
        const snapshot = await getDocs(staffQuery);
        const staffData = snapshot.docs.map(d => {
          const x = d.data() as any;
          const fullName = [x.personalInfo?.lastName, x.personalInfo?.firstName]
            .filter(Boolean).join(' ').trim();
          return {
            id: d.id,
            name: x.displayName?.trim() || fullName || `（名前未設定 ${x.employeeCode || d.id.slice(0, 6)}）`,
          };
        });
        staffData.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
        setAllStaffList(staffData);
      } catch (err) {
        console.error('Error fetching staff list:', err);
      }
    };
    fetchAllStaff();
  }, [shop]);

  // インセンティブ明細を取得
  const fetchIncentiveDetails = async () => {
    // 日付が空のまま走ると Date への変換で落ちる
    if (!shop || !isIncentiveMode || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const tz = shop.timezone || 'Asia/Tokyo';
      const boundary = resolveBoundaryHour(shop);
      const { start, end } = getBusinessDateRange(startDate, endDate, tz, boundary);

      const incentivesRef = collection(db, 'staffIncentives');
      const q = query(
        incentivesRef,
        where('shopId', '==', shop.id),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end)),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      // 税抜再計算用の設定
      const taxRate = resolveTaxRate(shop);
      const isTaxIncluded = shop.priceDisplayType === 'tax_included';

      const details: IncentiveDetail[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const grossAmount = data.grossAmount || 0;
        const checkoutTotal = data.checkoutTotal ?? grossAmount;
        let netSubtotal = data.netSubtotal || 0;

        // 既存データの税抜修正: netSubtotalがgrossAmountと同じ場合、税込→税抜を再計算
        if (isTaxIncluded && grossAmount > 0 && netSubtotal === grossAmount) {
          netSubtotal = Math.round(grossAmount / (1 + taxRate));
        }

        const costDeduction = data.costDeduction || 0;
        const isShopShare = data.isShopShare || false;
        const incentiveBase = isShopShare ? netSubtotal : Math.round(Math.max(0, netSubtotal - costDeduction));
        const shareRate = data.shareRate || 1;

        return {
          id: docSnap.id,
          staffId: data.staffId,
          staffName: data.staffName,
          date: data.date,
          checkoutId: data.checkoutId,
          checkoutType: data.checkoutType,
          grossAmount,
          checkoutTotal,
          netSubtotal,
          costDeduction,
          incentiveBase,
          shareRate,
          staffIncentiveBase: Math.round(incentiveBase * shareRate),
          backStatus: (data.backStatus as 'pending' | 'approved' | 'rejected') ?? 'pending',
          planContribution: data.planContribution || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });

      setIncentiveDetails(details);

      // スタッフリストを抽出
      const uniqueStaff = new Map<string, string>();
      details.forEach((d) => uniqueStaff.set(d.staffId, d.staffName));
      setStaffList(Array.from(uniqueStaff.entries()).map(([id, name]) => ({ id, name })));

      // スタッフ別サマリーを計算
      const summaryMap = new Map<string, IncentiveStaffSummary>();
      const staffCheckoutIds = new Map<string, Set<string>>();
      details.forEach((d) => {
        const existing = summaryMap.get(d.staffId) || {
          staffId: d.staffId,
          staffName: d.staffName,
          totalIncentiveBase: 0,
          checkoutCount: 0,
          daysWorked: 0,
          dailyAverage: 0,
          planContribution: 0,
        };
        existing.totalIncentiveBase += d.staffIncentiveBase;
        existing.planContribution += d.planContribution;
        // ユニークなcheckoutIdでカウント
        if (!staffCheckoutIds.has(d.staffId)) {
          staffCheckoutIds.set(d.staffId, new Set());
        }
        staffCheckoutIds.get(d.staffId)!.add(d.checkoutId);
        existing.checkoutCount = staffCheckoutIds.get(d.staffId)!.size;
        summaryMap.set(d.staffId, existing);
      });

      // 日数計算
      summaryMap.forEach((summary) => {
        const uniqueDays = new Set(
          details.filter(d => d.staffId === summary.staffId).map(d => d.date)
        );
        summary.daysWorked = uniqueDays.size;
        summary.dailyAverage = summary.daysWorked > 0
          ? Math.round(summary.totalIncentiveBase / summary.daysWorked)
          : 0;
      });

      setIncentiveStaffSummaries(Array.from(summaryMap.values()));
    } catch (err) {
      console.error('Error fetching incentive details:', err);
      setError('インセンティブ明細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // インセンティブ編集ダイアログを開く
  const openEditDialog = (detail: IncentiveDetail) => {
    setEditShareRate(Math.round(detail.shareRate * 100));
    setEditIncentiveBase(detail.incentiveBase);
    setEditDialog({ open: true, detail });
  };

  // インセンティブ編集を保存
  const handleSaveIncentiveEdit = async () => {
    if (!editDialog.detail) return;
    try {
      const newShareRate = editShareRate / 100;
      const newStaffIncentiveBase = Math.round(editIncentiveBase * newShareRate);
      await updateDoc(doc(db, 'staffIncentives', editDialog.detail.id), {
        shareRate: newShareRate,
        incentiveBase: editIncentiveBase,
        staffIncentiveBase: newStaffIncentiveBase,
      });
      setEditDialog({ open: false, detail: null });
      fetchIncentiveDetails();
    } catch (err) {
      console.error('Error updating incentive:', err);
      setError('インセンティブの更新に失敗しました');
    }
  };

  // インセンティブ削除
  const handleDeleteIncentive = async () => {
    if (!deleteDialog.detail) return;
    try {
      await deleteDoc(doc(db, 'staffIncentives', deleteDialog.detail.id));
      setDeleteDialog({ open: false, detail: null });
      fetchIncentiveDetails();
    } catch (err) {
      console.error('Error deleting incentive:', err);
      setError('インセンティブの削除に失敗しました');
    }
  };

  // バック明細を取得
  const fetchBackDetails = async () => {
    if (!shop || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const results: BackDetail[] = [];
      const unassigned: UnassignedCheckout[] = [];
      const tz = shop.timezone || 'Asia/Tokyo';
      const boundary = resolveBoundaryHour(shop);
      const { start, end } = getBusinessDateRange(startDate, endDate, tz, boundary);

      // POS会計からバック情報を取得
      const posCheckoutsRef = collection(db, 'posCheckouts');
      const posQuery = query(
        posCheckoutsRef,
        where('shopId', '==', shop.id),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end)),
        orderBy('createdAt', 'desc')
      );
      const posSnapshot = await getDocs(posQuery);

      for (const docSnap of posSnapshot.docs) {
        const data = docSnap.data();
        const salesStaff = data.salesStaff || [];

        if (salesStaff.length === 0) {
          unassigned.push({
            checkoutId: docSnap.id,
            checkoutType: 'pos',
            customerName: data.customerName || '匿名',
            total: data.total || 0,
            items: data.items || [],
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        }

        for (const line of extractBackLines(data, shop.costBackSettings?.defaultBackRate ?? 0.5)) {
          const matched = salesStaff.find((st: any) => st.staffId === line.staffId);
          results.push({
            checkoutId: docSnap.id,
            checkoutType: 'pos',
            customerName: data.customerName || '匿名',
            total: data.total || 0,
            staffId: line.staffId,
            staffName: line.staffName,
            shareRate: Math.round(line.shareRate * 1000) / 10,
            backRate: line.backRate * 100,
            backAmount: line.backAmount,
            status: matched?.backStatus || 'pending',
            createdAt: data.createdAt?.toDate() || new Date(),
            approvedAt: matched?.backApprovedAt?.toDate(),
            approvedBy: matched?.backApprovedBy,
            items: data.items || [],
          });
        }
      }

      // テーブル会計からバック情報を取得
      const checkoutsRef = collection(db, 'checkouts');
      const checkoutsQuery = query(
        checkoutsRef,
        where('shopId', '==', shop.id),
        where('status', '==', 'completed'),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end)),
        orderBy('createdAt', 'desc')
      );
      const checkoutsSnapshot = await getDocs(checkoutsQuery);

      for (const docSnap of checkoutsSnapshot.docs) {
        const data = docSnap.data();
        const salesStaff = data.salesStaff || [];

        if (salesStaff.length === 0) {
          unassigned.push({
            checkoutId: docSnap.id,
            checkoutType: 'table',
            customerName: data.tableName || `テーブル${data.tableNumber || ''}`,
            tableName: data.tableName,
            total: data.total || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        }

        for (const line of extractBackLines(data, shop.costBackSettings?.defaultBackRate ?? 0.5)) {
          const matched = salesStaff.find((st: any) => st.staffId === line.staffId);
          results.push({
            checkoutId: docSnap.id,
            checkoutType: 'table',
            customerName: data.tableName || `テーブル${data.tableNumber || ''}`,
            tableName: data.tableName,
            total: data.total || 0,
            staffId: line.staffId,
            staffName: line.staffName,
            shareRate: Math.round(line.shareRate * 1000) / 10,
            backRate: line.backRate * 100,
            backAmount: line.backAmount,
            status: matched?.backStatus || 'pending',
            createdAt: data.createdAt?.toDate() || new Date(),
            approvedAt: matched?.backApprovedAt?.toDate(),
            approvedBy: matched?.backApprovedBy,
          });
        }
      }

      // 日付でソート
      results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      unassigned.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setBackDetails(results);
      setUnassignedCheckouts(unassigned);

      // スタッフリストを抽出
      const uniqueStaff = new Map<string, string>();
      results.forEach((r) => uniqueStaff.set(r.staffId, r.staffName));
      setStaffList(Array.from(uniqueStaff.entries()).map(([id, name]) => ({ id, name })));

      // スタッフ別サマリーを計算
      const summaryMap = new Map<string, StaffSummary>();
      results.forEach((r) => {
        const existing = summaryMap.get(r.staffId) || {
          staffId: r.staffId,
          staffName: r.staffName,
          pendingCount: 0,
          pendingAmount: 0,
          approvedCount: 0,
          approvedAmount: 0,
        };

        if (r.status === 'pending') {
          existing.pendingCount++;
          existing.pendingAmount += r.backAmount;
        } else if (r.status === 'approved') {
          existing.approvedCount++;
          existing.approvedAmount += r.backAmount;
        }

        summaryMap.set(r.staffId, existing);
      });
      setStaffSummaries(Array.from(summaryMap.values()));
    } catch (err) {
      console.error('Error fetching back details:', err);
      setError('バック明細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isIncentiveMode) {
      fetchIncentiveDetails();
    } else {
      fetchBackDetails();
    }
  }, [shop, startDate, endDate]);

  // フィルタリング
  const filteredDetails = backDetails.filter((detail) => {
    if (filterStatus !== 'all' && detail.status !== filterStatus) return false;
    if (filterStaff !== 'all' && detail.staffId !== filterStaff) return false;
    return true;
  });

  // 選択されたアイテムのIDを生成
  const getItemKey = (detail: BackDetail) => `${detail.checkoutId}_${detail.staffId}`;

  // 全選択/解除
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pendingIds = new Set(
        filteredDetails.filter((d) => d.status === 'pending').map(getItemKey)
      );
      setSelectedIds(pendingIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  // 個別選択
  const handleSelect = (detail: BackDetail, checked: boolean) => {
    const key = getItemKey(detail);
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    setSelectedIds(newSelected);
  };

  // バック承認/却下
  const handleUpdateStatus = async (action: 'approve' | 'reject') => {
    if (selectedIds.size === 0) return;

    setLoading(true);
    try {
      const updates: { checkoutId: string; checkoutType: string; staffId: string }[] = [];

      selectedIds.forEach((key) => {
        const detail = backDetails.find((d) => getItemKey(d) === key);
        if (detail) {
          updates.push({
            checkoutId: detail.checkoutId,
            checkoutType: detail.checkoutType,
            staffId: detail.staffId,
          });
        }
      });

      // Firestoreを更新
      for (const update of updates) {
        const collectionName = update.checkoutType === 'pos' ? 'posCheckouts' : 'checkouts';
        const docRef = doc(db, collectionName, update.checkoutId);

        // 現在のドキュメントを取得
        const docSnap = await getDocs(
          query(collection(db, collectionName), where('__name__', '==', update.checkoutId))
        );

        if (!docSnap.empty) {
          const data = docSnap.docs[0].data();
          const salesStaff = data.salesStaff || [];

          // 該当スタッフのステータスを更新
          const updatedSalesStaff = salesStaff.map((staff: any) => {
            if (staff.staffId === update.staffId) {
              return {
                ...staff,
                backStatus: action === 'approve' ? 'approved' : 'rejected',
                backApprovedAt: Timestamp.now(),
                backApprovedBy: shop?.id,
              };
            }
            return staff;
          });

          await updateDoc(docRef, { salesStaff: updatedSalesStaff });
        }
      }

      setSelectedIds(new Set());
      setConfirmDialog({ open: false, action: null });
      await fetchBackDetails();
    } catch (err) {
      console.error('Error updating back status:', err);
      setError('ステータスの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // ========== バック編集機能 ==========

  const openEditBackDialog = (detail: BackDetail) => {
    setEditBackShareRate(detail.shareRate);
    setEditBackBackRate(detail.backRate);
    setEditBackDialog({ open: true, detail });
  };

  const handleSaveBackEdit = async () => {
    const detail = editBackDialog.detail;
    if (!detail) return;

    try {
      const collectionName = detail.checkoutType === 'pos' ? 'posCheckouts' : 'checkouts';
      const docRef = doc(db, collectionName, detail.checkoutId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const salesStaff = data.salesStaff || [];

        const updatedSalesStaff = salesStaff.map((staff: any) => {
          if (staff.staffId === detail.staffId) {
            return {
              ...staff,
              shareRate: editBackShareRate,
              backRate: pctToRate(editBackBackRate),
              backStatus: 'pending',
            };
          }
          return staff;
        });

        await updateDoc(docRef, { salesStaff: updatedSalesStaff });
      }

      setEditBackDialog({ open: false, detail: null });
      await fetchBackDetails();
    } catch (err) {
      console.error('Error updating back:', err);
      setError('バック情報の更新に失敗しました');
    }
  };

  // ========== バック削除機能 ==========

  const handleDeleteBack = async () => {
    const detail = deleteBackDialog.detail;
    if (!detail) return;

    try {
      const collectionName = detail.checkoutType === 'pos' ? 'posCheckouts' : 'checkouts';
      const docRef = doc(db, collectionName, detail.checkoutId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const salesStaff = data.salesStaff || [];

        const updatedSalesStaff = salesStaff.filter(
          (staff: any) => staff.staffId !== detail.staffId
        );

        if (updatedSalesStaff.length === 0) {
          await updateDoc(docRef, { salesStaff: deleteField() });
        } else {
          await updateDoc(docRef, { salesStaff: updatedSalesStaff });
        }
      }

      setDeleteBackDialog({ open: false, detail: null });
      await fetchBackDetails();
    } catch (err) {
      console.error('Error deleting back:', err);
      setError('バック情報の削除に失敗しました');
    }
  };

  // ========== スタッフ後付け機能 ==========

  const openAssignDialog = (checkout: UnassignedCheckout) => {
    setAssignStaffSelections([]);
    setAssignDialog({ open: true, checkout });
  };

  const handleToggleAssignStaff = (staffId: string, staffName: string) => {
    setAssignStaffSelections(prev => {
      const exists = prev.find(s => s.staffId === staffId);
      if (exists) {
        const updated = prev.filter(s => s.staffId !== staffId);
        // 均等分配を再計算
        if (updated.length > 0) {
          const equalShare = 100 / updated.length;
          return updated.map(s => ({ ...s, shareRate: equalShare }));
        }
        return updated;
      } else {
        const updated = [...prev, { staffId, staffName, shareRate: 0 }];
        const equalShare = 100 / updated.length;
        return updated.map(s => ({ ...s, shareRate: equalShare }));
      }
    });
  };

  const handleSaveAssignment = async () => {
    const checkout = assignDialog.checkout;
    if (!checkout || assignStaffSelections.length === 0) return;

    try {
      const collectionName = checkout.checkoutType === 'pos' ? 'posCheckouts' : 'checkouts';
      const docRef = doc(db, collectionName, checkout.checkoutId);
      const defaultBackRate = shop?.costBackSettings?.defaultBackRate || 0.5;

      const salesStaff = assignStaffSelections.map(s => ({
        staffId: s.staffId,
        staffName: s.staffName,
        shareRate: s.shareRate,
        backRate: defaultBackRate,
      }));

      await updateDoc(docRef, { salesStaff });

      setAssignDialog({ open: false, checkout: null });
      setAssignStaffSelections([]);
      await fetchBackDetails();
    } catch (err) {
      console.error('Error assigning staff:', err);
      setError('スタッフの設定に失敗しました');
    }
  };

  // 先月を選択
  const selectLastMonth = () => {
    const shopNow = nowInShopTimezone(shop?.timezone);
    const lastMonth = subMonths(shopNow, 1);
    setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
  };

  // 今月を選択
  const selectThisMonth = () => {
    const shopNow = nowInShopTimezone(shop?.timezone);
    setStartDate(format(startOfMonth(shopNow), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(shopNow), 'yyyy-MM-dd'));
  };

  const pendingCount = filteredDetails.filter((d) => d.status === 'pending').length;
  const pendingTotal = filteredDetails
    .filter((d) => d.status === 'pending')
    .reduce((sum, d) => sum + d.backAmount, 0);

  // インセンティブモード用のフィルタリング
  const filteredIncentiveDetails = incentiveDetails.filter((detail) => {
    if (filterStaff !== 'all' && detail.staffId !== filterStaff) return false;
    return true;
  });

  // バック編集ダイアログの計算値
  const editBackAmount = editBackDialog.detail
    ? editBackDialog.detail.total * (editBackShareRate / 100) * (editBackBackRate / 100)
    : 0;

  // インセンティブモードの場合は専用UIを表示
  if (isIncentiveMode) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          バック承認
        </Typography>

        {/* 累進を使う店にも承認の導線を出す。
            以前は編集と削除しかなく、夜職の標準設定では承認できなかった。 */}
        {selectedIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">
              {selectedIds.size} 件を選択中
            </Typography>
            <Button
              variant="contained" color="success" size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => setConfirmDialog({ open: true, action: 'approve' })}
            >
              承認する
            </Button>
            <Button
              variant="outlined" color="error" size="small"
              onClick={() => setConfirmDialog({ open: true, action: 'reject' })}
            >
              却下する
            </Button>
            <Button size="small" onClick={() => setSelectedIds(new Set())}>選択を解除</Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* スタッフ別サマリー */}
        {incentiveStaffSummaries.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {incentiveStaffSummaries.map((summary) => (
              <Grid item xs={12} sm={6} md={4} key={summary.staffId}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {summary.staffName}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">月間基準額</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ¥{summary.totalIncentiveBase.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">日平均</Typography>
                        <Typography variant="body2">
                          ¥{summary.dailyAverage.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">会計件数</Typography>
                        <Typography variant="body2">
                          {summary.checkoutCount}件（{summary.daysWorked}日）
                        </Typography>
                      </Box>
                      {summary.planContribution > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">プラン貢献</Typography>
                          <Typography variant="body2">
                            ¥{summary.planContribution.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* フィルター */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="date"
              label="開始日"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="終了日"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" size="small" onClick={selectThisMonth}>
              今月
            </Button>
            <Button variant="outlined" size="small" onClick={selectLastMonth}>
              先月
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>スタッフ</InputLabel>
              <Select
                value={filterStaff}
                label="スタッフ"
                onChange={(e) => setFilterStaff(e.target.value)}
              >
                <MenuItem value="all">全スタッフ</MenuItem>
                {staffList.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {staff.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="更新">
              <IconButton onClick={fetchIncentiveDetails}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* インセンティブ明細テーブル */}
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* 累進を使う店では承認の導線が無く、編集と削除しかできなかった。
                      夜職の標準設定（累進あり）で「バック承認」が使えない状態だった。 */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedIds.size > 0 &&
                        selectedIds.size < filteredIncentiveDetails.filter((d) => (d.backStatus ?? 'pending') === 'pending').length
                      }
                      checked={
                        filteredIncentiveDetails.filter((d) => (d.backStatus ?? 'pending') === 'pending').length > 0 &&
                        selectedIds.size === filteredIncentiveDetails.filter((d) => (d.backStatus ?? 'pending') === 'pending').length
                      }
                      disabled={filteredIncentiveDetails.filter((d) => (d.backStatus ?? 'pending') === 'pending').length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(
                            filteredIncentiveDetails
                              .filter((d) => (d.backStatus ?? 'pending') === 'pending')
                              .map((d) => `${d.checkoutId}_${d.staffId}`)
                          ));
                        } else setSelectedIds(new Set());
                      }}
                    />
                  </TableCell>
                  <TableCell>日時</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>スタッフ</TableCell>
                  <TableCell align="right">売上（税込）</TableCell>
                  <TableCell align="right">小計（税抜）</TableCell>
                  <TableCell align="right">原価控除</TableCell>
                  <TableCell align="right">基準額</TableCell>
                  <TableCell align="right">分配率</TableCell>
                  <TableCell align="right">スタッフ基準額</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncentiveDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncentiveDetails.map((detail) => {
                    const key = `${detail.checkoutId}_${detail.staffId}`;
                    const st = detail.backStatus ?? 'pending';
                    return (
                    <TableRow key={detail.id}
                      sx={{ bgcolor: st === 'approved' ? 'success.lighter' : st === 'rejected' ? 'error.lighter' : undefined }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(key)}
                          disabled={st !== 'pending'}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(key); else next.delete(key);
                            setSelectedIds(next);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {format(detail.createdAt, 'M/d HH:mm', { locale: ja })}
                      </TableCell>
                      <TableCell>
                        {detail.checkoutType === 'pos' ? (
                          <Chip icon={<POSIcon />} label="POS" size="small" color="primary" variant="outlined" />
                        ) : (
                          <Chip icon={<TableIcon />} label="テーブル" size="small" color="secondary" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{detail.staffName}</TableCell>
                      <TableCell align="right">¥{detail.checkoutTotal.toLocaleString()}</TableCell>
                      <TableCell align="right">¥{detail.netSubtotal.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {detail.costDeduction > 0 ? `-¥${detail.costDeduction.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell align="right">¥{detail.incentiveBase.toLocaleString()}</TableCell>
                      <TableCell align="right">{Math.round(detail.shareRate * 100)}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ¥{detail.staffIncentiveBase.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="編集">
                          <IconButton size="small" onClick={() => openEditDialog(detail)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, detail })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* 合計 */}
        {filteredIncentiveDetails.length > 0 && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
              <Typography>
                売上合計: <strong>¥{Array.from(new Set(filteredIncentiveDetails.map(d => d.checkoutId))).reduce((sum, cid) => {
                  const first = filteredIncentiveDetails.find(d => d.checkoutId === cid);
                  return sum + (first?.checkoutTotal || 0);
                }, 0).toLocaleString()}</strong>
              </Typography>
              <Typography>
                基準額合計: <strong>¥{filteredIncentiveDetails.reduce((sum, d) => sum + d.staffIncentiveBase, 0).toLocaleString()}</strong>
              </Typography>
              <Typography>
                会計件数: <strong>{new Set(filteredIncentiveDetails.map(d => d.checkoutId)).size}件</strong>
              </Typography>
            </Box>
          </Paper>
        )}

        {/* 編集ダイアログ */}
        <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, detail: null })} maxWidth="sm" fullWidth>
          <DialogTitle>インセンティブ訂正</DialogTitle>
          <DialogContent>
            {editDialog.detail && (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {editDialog.detail.staffName} - {editDialog.detail.date} ({editDialog.detail.checkoutType === 'pos' ? 'POS' : 'テーブル'})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    売上（税込）: ¥{editDialog.detail.checkoutTotal.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    小計（税抜）: ¥{editDialog.detail.netSubtotal.toLocaleString()}
                  </Typography>
                </Box>
                <TextField
                  label="基準額"
                  type="number"
                  value={editIncentiveBase}
                  onChange={(e) => setEditIncentiveBase(Number(e.target.value))}
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>¥</Typography> }}
                />
                <TextField
                  label="分配率（%）"
                  type="number"
                  value={editShareRate}
                  onChange={(e) => setEditShareRate(Number(e.target.value))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, max: 100 }}
                  InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }}
                />
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">
                    訂正後スタッフ基準額: <strong>¥{Math.round(editIncentiveBase * editShareRate / 100).toLocaleString()}</strong>
                  </Typography>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ open: false, detail: null })}>キャンセル</Button>
            <Button variant="contained" onClick={handleSaveIncentiveEdit}>保存</Button>
          </DialogActions>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, detail: null })}>
          <DialogTitle>インセンティブ削除</DialogTitle>
          <DialogContent>
            {deleteDialog.detail && (
              <Typography>
                {deleteDialog.detail.staffName}の{deleteDialog.detail.date}のインセンティブ記録（¥{deleteDialog.detail.staffIncentiveBase.toLocaleString()}）を削除しますか？
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, detail: null })}>キャンセル</Button>
            <Button variant="contained" color="error" onClick={handleDeleteIncentive}>削除</Button>
          </DialogActions>
        </Dialog>
        {/* 承認/却下の確認。累進モードでも押せるようにここにも置く。
            以前は下の（累進を使わない店向けの）return 側にしか無く、
            ボタンを足しても確認ダイアログが出なかった。 */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
          <DialogTitle>
            {confirmDialog.action === 'approve' ? 'バックを承認しますか？' : 'バックを却下しますか？'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {selectedIds.size} 件のバックを
              {confirmDialog.action === 'approve' ? '承認' : '却下'}します。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              承認した記録は残ります。取り消すには却下に切り替えてください。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, action: null })}>やめる</Button>
            <Button
              variant="contained"
              color={confirmDialog.action === 'approve' ? 'success' : 'error'}
              onClick={() => handleUpdateStatus(confirmDialog.action!)}
            >
              {confirmDialog.action === 'approve' ? '承認する' : '却下する'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        バック承認管理
        <Chip
          label={`未承認: ${pendingCount}件`}
          color="warning"
          size="small"
          sx={{ ml: 2 }}
        />
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* スタッフ別サマリー */}
      {staffSummaries.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {staffSummaries.map((summary) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={summary.staffId}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {summary.staffName}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="warning.main">
                        未承認
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ¥{summary.pendingAmount.toLocaleString()}
                        <Typography component="span" variant="caption" color="textSecondary">
                          ({summary.pendingCount}件)
                        </Typography>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="success.main">
                        承認済
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ¥{summary.approvedAmount.toLocaleString()}
                        <Typography component="span" variant="caption" color="textSecondary">
                          ({summary.approvedCount}件)
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* フィルター */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            type="date"
            label="開始日"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="終了日"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" size="small" onClick={selectThisMonth}>
            今月
          </Button>
          <Button variant="outlined" size="small" onClick={selectLastMonth}>
            先月
          </Button>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={filterStatus}
              label="ステータス"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="pending">未承認</MenuItem>
              <MenuItem value="approved">承認済</MenuItem>
              <MenuItem value="rejected">却下</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>スタッフ</InputLabel>
            <Select
              value={filterStaff}
              label="スタッフ"
              onChange={(e) => setFilterStaff(e.target.value)}
            >
              <MenuItem value="all">全スタッフ</MenuItem>
              {staffList.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  {staff.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="更新">
            <IconButton onClick={fetchBackDetails}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* スタッフ未設定の会計 */}
      {unassignedCheckouts.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            スタッフ未設定の会計
            <Chip label={`${unassignedCheckouts.length}件`} size="small" color="info" />
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>顧客/テーブル</TableCell>
                  <TableCell align="right">売上</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unassignedCheckouts.map((checkout) => (
                  <TableRow key={checkout.checkoutId}>
                    <TableCell>
                      {format(checkout.createdAt, 'M/d HH:mm', { locale: ja })}
                    </TableCell>
                    <TableCell>
                      {checkout.checkoutType === 'pos' ? (
                        <Chip icon={<POSIcon />} label="POS" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip icon={<TableIcon />} label="テーブル" size="small" color="secondary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{checkout.customerName}</TableCell>
                    <TableCell align="right">¥{checkout.total.toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => openAssignDialog(checkout)}
                      >
                        スタッフ設定
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* アクションボタン */}
      {selectedIds.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography>
              {selectedIds.size}件選択中（合計: ¥
              {backDetails
                .filter((d) => selectedIds.has(getItemKey(d)))
                .reduce((sum, d) => sum + d.backAmount, 0)
                .toLocaleString()}
              ）
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setConfirmDialog({ open: true, action: 'approve' })}
            >
              承認する
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setConfirmDialog({ open: true, action: 'reject' })}
            >
              却下する
            </Button>
          </Box>
        </Paper>
      )}

      {/* バック明細テーブル */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.size > 0 &&
                      selectedIds.size < filteredDetails.filter((d) => d.status === 'pending').length
                    }
                    checked={
                      filteredDetails.filter((d) => d.status === 'pending').length > 0 &&
                      selectedIds.size === filteredDetails.filter((d) => d.status === 'pending').length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={filteredDetails.filter((d) => d.status === 'pending').length === 0}
                  />
                </TableCell>
                <TableCell>日時</TableCell>
                <TableCell>種別</TableCell>
                <TableCell>顧客/テーブル</TableCell>
                <TableCell>スタッフ</TableCell>
                <TableCell align="right">売上</TableCell>
                <TableCell align="right">分配率</TableCell>
                <TableCell align="right">バック率</TableCell>
                <TableCell align="right">バック額</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDetails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    データがありません
                  </TableCell>
                </TableRow>
              ) : (
                filteredDetails.map((detail) => {
                  const key = getItemKey(detail);
                  return (
                    <TableRow
                      key={key}
                      sx={{
                        bgcolor:
                          detail.status === 'approved'
                            ? 'success.lighter'
                            : detail.status === 'rejected'
                            ? 'error.lighter'
                            : undefined,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(key)}
                          onChange={(e) => handleSelect(detail, e.target.checked)}
                          disabled={detail.status !== 'pending'}
                        />
                      </TableCell>
                      <TableCell>
                        {format(detail.createdAt, 'M/d HH:mm', { locale: ja })}
                      </TableCell>
                      <TableCell>
                        {detail.checkoutType === 'pos' ? (
                          <Chip
                            icon={<POSIcon />}
                            label="POS"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<TableIcon />}
                            label="テーブル"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>{detail.customerName}</TableCell>
                      <TableCell>{detail.staffName}</TableCell>
                      <TableCell align="right">¥{detail.total.toLocaleString()}</TableCell>
                      <TableCell align="right">{detail.shareRate}%</TableCell>
                      <TableCell align="right">{detail.backRate}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ¥{detail.backAmount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {detail.status === 'pending' && (
                          <Chip label="未承認" size="small" color="warning" />
                        )}
                        {detail.status === 'approved' && (
                          <Chip label="承認済" size="small" color="success" />
                        )}
                        {detail.status === 'rejected' && (
                          <Chip label="却下" size="small" color="error" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="編集">
                          <IconButton size="small" onClick={() => openEditBackDialog(detail)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton size="small" color="error" onClick={() => setDeleteBackDialog({ open: true, detail })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* 合計 */}
      {filteredDetails.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            <Typography>
              未承認合計: <strong>¥{pendingTotal.toLocaleString()}</strong>
            </Typography>
            <Typography>
              承認済合計:{' '}
              <strong>
                ¥
                {filteredDetails
                  .filter((d) => d.status === 'approved')
                  .reduce((sum, d) => sum + d.backAmount, 0)
                  .toLocaleString()}
              </strong>
            </Typography>
          </Box>
        </Paper>
      )}

      {/* 承認/却下確認ダイアログ */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
        <DialogTitle>
          {confirmDialog.action === 'approve' ? 'バックを承認しますか？' : 'バックを却下しますか？'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedIds.size}件のバック（合計: ¥
            {backDetails
              .filter((d) => selectedIds.has(getItemKey(d)))
              .reduce((sum, d) => sum + d.backAmount, 0)
              .toLocaleString()}
            ）を{confirmDialog.action === 'approve' ? '承認' : '却下'}します。
          </Typography>
          {confirmDialog.action === 'approve' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              承認するとスタッフアプリのマイシフト画面に反映されます。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>キャンセル</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            onClick={() => handleUpdateStatus(confirmDialog.action!)}
          >
            {confirmDialog.action === 'approve' ? '承認する' : '却下する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* バック編集ダイアログ */}
      <Dialog open={editBackDialog.open} onClose={() => setEditBackDialog({ open: false, detail: null })} maxWidth="sm" fullWidth>
        <DialogTitle>バック訂正</DialogTitle>
        <DialogContent>
          {editBackDialog.detail && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">日時</Typography>
                    <Typography>{format(editBackDialog.detail.createdAt, 'yyyy/MM/dd HH:mm')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">種別</Typography>
                    <Typography>{editBackDialog.detail.checkoutType === 'pos' ? 'POS' : 'テーブル'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">スタッフ</Typography>
                    <Typography>{editBackDialog.detail.staffName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">売上</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>¥{editBackDialog.detail.total.toLocaleString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <TextField
                label="分配率（%）"
                type="number"
                value={editBackShareRate}
                onChange={(e) => setEditBackShareRate(Number(e.target.value))}
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 100 }}
                InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }}
              />
              <TextField
                label="バック率（%）"
                type="number"
                value={editBackBackRate}
                onChange={(e) => setEditBackBackRate(Number(e.target.value))}
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 100 }}
                InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }}
              />
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">
                  訂正後バック額: <strong>¥{Math.round(editBackAmount).toLocaleString()}</strong>
                </Typography>
              </Paper>
              <Alert severity="info" variant="outlined">
                訂正するとステータスが未承認に戻ります。
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBackDialog({ open: false, detail: null })}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveBackEdit}>保存</Button>
        </DialogActions>
      </Dialog>

      {/* バック削除確認ダイアログ */}
      <Dialog open={deleteBackDialog.open} onClose={() => setDeleteBackDialog({ open: false, detail: null })}>
        <DialogTitle>バック削除</DialogTitle>
        <DialogContent>
          {deleteBackDialog.detail && (
            <Typography>
              {deleteBackDialog.detail.staffName}のバック記録（¥{Math.round(deleteBackDialog.detail.backAmount).toLocaleString()}）を削除しますか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBackDialog({ open: false, detail: null })}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDeleteBack}>削除</Button>
        </DialogActions>
      </Dialog>

      {/* スタッフ設定ダイアログ */}
      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, checkout: null })} maxWidth="sm" fullWidth>
        <DialogTitle>スタッフ設定</DialogTitle>
        <DialogContent>
          {assignDialog.checkout && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">日時</Typography>
                    <Typography>{format(assignDialog.checkout.createdAt, 'yyyy/MM/dd HH:mm')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">種別</Typography>
                    <Typography>{assignDialog.checkout.checkoutType === 'pos' ? 'POS' : 'テーブル'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">顧客/テーブル</Typography>
                    <Typography>{assignDialog.checkout.customerName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">売上</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>¥{assignDialog.checkout.total.toLocaleString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2">担当スタッフを選択</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allStaffList.map((staff) => {
                  const isSelected = assignStaffSelections.some(s => s.staffId === staff.id);
                  return (
                    <Chip
                      key={staff.id}
                      label={staff.name}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => handleToggleAssignStaff(staff.id, staff.name)}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
                {allStaffList.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    スタッフが登録されていません
                  </Typography>
                )}
              </Box>

              {assignStaffSelections.length > 1 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>分配率設定</Typography>
                  {assignStaffSelections.map((staff, index) => (
                    <Box
                      key={staff.staffId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={staff.staffName}
                      >
                        {staff.staffName}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={Math.round(staff.shareRate)}
                        onChange={(e) => {
                          const newVal = Number(e.target.value);
                          setAssignStaffSelections(prev =>
                            prev.map((s, i) => i === index ? { ...s, shareRate: newVal } : s)
                          );
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        InputProps={{ endAdornment: <Typography sx={{ ml: 0.5 }}>%</Typography> }}
                        sx={{ width: 120, flexShrink: 0 }}
                      />
                    </Box>
                  ))}
                  {Math.round(assignStaffSelections.reduce((sum, s) => sum + s.shareRate, 0)) !== 100 && (
                    <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                      分配率の合計が100%になるように設定してください（現在: {Math.round(assignStaffSelections.reduce((sum, s) => sum + s.shareRate, 0))}%）
                    </Alert>
                  )}
                </Box>
              )}

              {assignStaffSelections.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    バック率: {((shop?.costBackSettings?.defaultBackRate || 0.5) * 100).toFixed(0)}%（デフォルト）
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, checkout: null })}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSaveAssignment}
            disabled={assignStaffSelections.length === 0}
          >
            設定する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackApproval;
